import { useMemo, useRef, useState } from 'react'
import {
  Building2,
  ChevronRight,
  Loader2,
  Plus,
  RefreshCw,
  Search,
  Trash2,
  UserRoundPlus,
  X,
} from 'lucide-react'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Typography } from '@/components/ui/typography'
import { AdminUsersToolbar } from '@/components/custom/admin/admin-users-toolbar'
import {
  AdminUserTable,
  type AdminDirectoryRow,
} from '@/components/custom/admin/admin-user-table'
import { EditPortcoUserSheet } from '@/components/custom/admin/edit-portco-user-sheet'
import { InviteUserSheet } from '@/components/custom/admin/invite-user-sheet'
import { formatAllowedAppsSummary } from '@/components/custom/admin/user-app-access-fields'
import {
  type AdminUser,
  useAllowedDomains,
  useCreateAllowedDomain,
  useDeleteAllowedDomain,
} from '@/hooks/use-admin'
import {
  useAllPortalInvitations,
  useCompanyChangePortalUserAccess,
  useCompanyChangePortalUserOgsmAccess,
  useCompanyChangePortalUserRole,
  useCompanyPortalUsers,
  useCompanyRemovePortalUser,
  useCompanyResendPortalInvitation,
  useCompanyRevokePortalInvitation,
} from '@/hooks/use-portal'
import { useCompaniesDropdownSearch } from '@/hooks/use-reference-data'
import { isPortalOgsmEnabled } from '@/configs/feature-flags'
import { initialsOf } from '@/lib/initials'
import { showError, showSuccess } from '@/lib/toast'
import { cn } from '@/lib/utils'
import type { PortalInvitation } from '@/services/portal.service'
import type { AllowedApp } from '@/types/api'

const DOMAIN_REGEX =
  /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/

const PORTCO_ROLE_OPTIONS = [
  { value: 'PORTCO_ADMIN', label: 'Admin' },
  { value: 'PORTCO_STANDARD', label: 'Member' },
]

const ROLE_LABELS = new Map(
  PORTCO_ROLE_OPTIONS.map((option) => [option.value, option.label])
)

interface CompanyInfo {
  id: string
  name: string
  users: AdminUser[]
  invitations: PortalInvitation[]
  matchCount?: number
}

interface PortcoAccessTabProps {
  users: AdminUser[]
}

export function PortcoAccessTab({ users }: PortcoAccessTabProps) {
  const [search, setSearch] = useState('')
  const [companySearch, setCompanySearch] = useState('')
  const [role, setRole] = useState('all')
  const [appAccess, setAppAccess] = useState('all')
  const [status, setStatus] = useState('all')
  const [companyScope, setCompanyScope] = useState<'access' | 'all'>('access')
  const [selectedCompanyId, setSelectedCompanyId] = useState('')
  const [inviteOpen, setInviteOpen] = useState(false)
  const inviteButtonRef = useRef<HTMLButtonElement>(null)
  const allInvitations = useAllPortalInvitations()
  const companiesQuery = useCompaniesDropdownSearch({
    search: '',
    enabled: true,
  })

  const externalUsers = useMemo(
    () => users.filter((user) => user.userType === 'EXTERNAL'),
    [users]
  )

  const companies = useMemo(() => {
    const invitations = allInvitations.data?.data ?? []
    return (companiesQuery.data?.data ?? [])
      .map(
        (company): CompanyInfo => ({
          id: company.id,
          name: company.displayName,
          users: externalUsers.filter((user) =>
            user.companyAssignments.some(
              (assignment) =>
                assignment.companyId === company.id && assignment.isActive
            )
          ),
          invitations: invitations.filter(
            (invitation) => invitation.companyId === company.id
          ),
        })
      )
      .sort((left, right) => left.name.localeCompare(right.name))
  }, [allInvitations.data?.data, companiesQuery.data?.data, externalUsers])

  const visibleCompanies = useMemo(() => {
    const globalQuery = search.trim().toLowerCase()
    const railQuery = companySearch.trim().toLowerCase()
    const peopleFiltersActive =
      role !== 'all' || appAccess !== 'all' || status !== 'all'

    return companies.flatMap((company) => {
      const hasAccess =
        company.users.length > 0 || company.invitations.length > 0
      if (companyScope === 'access' && !hasAccess) return []
      if (railQuery && !company.name.toLowerCase().includes(railQuery))
        return []

      const companyNameMatches = company.name
        .toLowerCase()
        .includes(globalQuery)
      const personQuery = companyNameMatches ? '' : globalQuery
      const matchingUsers = company.users.filter((user) =>
        userMatchesFilters(user, role, appAccess, status, personQuery)
      )
      const matchingInvitations = company.invitations.filter((invitation) =>
        invitationMatchesFilters(
          invitation,
          role,
          appAccess,
          status,
          personQuery
        )
      )
      const matchCount = matchingUsers.length + matchingInvitations.length

      if (peopleFiltersActive && matchCount === 0) return []
      if (globalQuery && !companyNameMatches && matchCount === 0) return []

      return [
        {
          ...company,
          matchCount:
            peopleFiltersActive || (globalQuery && !companyNameMatches)
              ? matchCount
              : undefined,
        },
      ]
    })
  }, [appAccess, companies, companyScope, companySearch, role, search, status])

  const effectiveSelectedCompanyId = visibleCompanies.some(
    (company) => company.id === selectedCompanyId
  )
    ? selectedCompanyId
    : (visibleCompanies[0]?.id ?? '')

  const selectedCompany =
    companies.find((company) => company.id === effectiveSelectedCompanyId) ??
    null

  return (
    <>
      <div className="flex w-full max-w-7xl flex-col gap-5">
        <AdminUsersToolbar
          search={search}
          searchPlaceholder="Search people or companies"
          onSearchChange={setSearch}
          role={role}
          roleOptions={PORTCO_ROLE_OPTIONS}
          onRoleChange={setRole}
          appAccess={appAccess}
          onAppAccessChange={setAppAccess}
          status={status}
          statusOptions={[
            { value: 'all', label: 'All statuses' },
            { value: 'active', label: 'Active' },
            { value: 'inactive', label: 'Inactive' },
            { value: 'invited', label: 'Invited' },
          ]}
          onStatusChange={setStatus}
          onInvite={() => setInviteOpen(true)}
          inviteButtonRef={inviteButtonRef}
        />

        {companiesQuery.isError || allInvitations.isError ? (
          <DirectoryError
            onRetry={() => {
              void companiesQuery.refetch()
              void allInvitations.refetch()
            }}
          />
        ) : companiesQuery.isLoading ? (
          <DirectorySkeleton />
        ) : (
          <div className="min-w-0 overflow-hidden rounded-xl border border-primary/15 bg-background lg:grid lg:min-h-[560px] lg:grid-cols-[320px_minmax(0,1fr)]">
            <CompanyRail
              companies={visibleCompanies}
              selectedCompanyId={effectiveSelectedCompanyId}
              onSelect={setSelectedCompanyId}
              scope={companyScope}
              onScopeChange={setCompanyScope}
              search={companySearch}
              onSearchChange={setCompanySearch}
            />

            <div className="border-b border-primary/10 p-4 lg:hidden">
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <Typography
                  variant="body-sm"
                  className="font-medium text-primary/70"
                >
                  Company
                </Typography>
                <CompanyScope
                  scope={companyScope}
                  onScopeChange={setCompanyScope}
                />
              </div>
              <Select
                value={effectiveSelectedCompanyId}
                onValueChange={setSelectedCompanyId}
              >
                <SelectTrigger aria-label="Select company" className="h-11">
                  <SelectValue placeholder="Select a company" />
                </SelectTrigger>
                <SelectContent>
                  {visibleCompanies.map((company) => (
                    <SelectItem key={company.id} value={company.id}>
                      {company.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {selectedCompany ? (
              <CompanyDetail
                key={selectedCompany.id}
                company={selectedCompany}
                role={role}
                appAccess={appAccess}
                status={status}
                search={search}
              />
            ) : (
              <div className="flex min-h-80 items-center justify-center px-6 text-center">
                <Typography variant="body-sm" className="text-primary/70">
                  No companies match these filters.
                </Typography>
              </div>
            )}
          </div>
        )}

        <Typography
          variant="body-sm"
          className="text-primary/70"
          aria-live="polite"
        >
          Showing {visibleCompanies.length} of {companies.length} companies
        </Typography>
      </div>

      <InviteUserSheet
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        mode="portco-picker"
        returnFocusRef={inviteButtonRef}
        askOgsmAccess={isPortalOgsmEnabled()}
      />
    </>
  )
}

function CompanyRail({
  companies,
  selectedCompanyId,
  onSelect,
  scope,
  onScopeChange,
  search,
  onSearchChange,
}: {
  companies: CompanyInfo[]
  selectedCompanyId: string
  onSelect: (companyId: string) => void
  scope: 'access' | 'all'
  onScopeChange: (scope: 'access' | 'all') => void
  search: string
  onSearchChange: (value: string) => void
}) {
  return (
    <aside
      aria-label="PortCo company directory"
      className="hidden min-w-0 border-r border-primary/10 lg:block"
    >
      <div className="border-b border-primary/10 p-4">
        <div className="mb-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Building2 className="size-4 text-primary" aria-hidden="true" />
            <Typography variant="h6" className="font-serif">
              Companies
            </Typography>
          </div>
          <CompanyScope scope={scope} onScopeChange={onScopeChange} />
        </div>
        <div className="relative">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-primary/55"
            aria-hidden="true"
          />
          <Input
            value={search}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder="Search companies"
            aria-label="Search companies"
            className="h-10 pl-10"
          />
        </div>
      </div>

      <div className="max-h-[610px] overflow-y-auto">
        {companies.map((company) => {
          const selected = company.id === selectedCompanyId
          return (
            <button
              type="button"
              key={company.id}
              onClick={() => onSelect(company.id)}
              aria-current={selected ? 'true' : undefined}
              className={cn(
                'flex min-h-20 w-full items-center gap-3 border-b border-primary/10 px-4 py-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
                selected
                  ? 'border-l-2 border-l-secondary bg-secondary/[0.06]'
                  : 'hover:bg-primary/[0.025]'
              )}
            >
              <Building2
                className={cn(
                  'size-5 shrink-0',
                  selected ? 'text-secondary' : 'text-primary'
                )}
                aria-hidden="true"
              />
              <div className="min-w-0 flex-1">
                <Typography
                  variant="body"
                  className="truncate font-serif text-base text-primary"
                >
                  {company.name}
                </Typography>
                <Typography variant="body-sm" className="mt-1 text-primary/70">
                  {company.matchCount !== undefined
                    ? `${company.matchCount} ${company.matchCount === 1 ? 'match' : 'matches'} · `
                    : ''}
                  {company.users.length === 0
                    ? 'No users'
                    : `${company.users.length} ${company.users.length === 1 ? 'user' : 'users'}`}
                  {company.invitations.length > 0
                    ? ` · ${company.invitations.length} pending`
                    : ''}
                </Typography>
              </div>
              <ChevronRight
                className="size-4 shrink-0 text-primary/55"
                aria-hidden="true"
              />
            </button>
          )
        })}
      </div>
    </aside>
  )
}

function CompanyScope({
  scope,
  onScopeChange,
}: {
  scope: 'access' | 'all'
  onScopeChange: (scope: 'access' | 'all') => void
}) {
  return (
    <div
      role="group"
      aria-label="Company scope"
      className="inline-flex rounded-md border border-primary/15 p-0.5"
    >
      <ScopeButton
        selected={scope === 'access'}
        onClick={() => onScopeChange('access')}
      >
        With access
      </ScopeButton>
      <ScopeButton
        selected={scope === 'all'}
        onClick={() => onScopeChange('all')}
      >
        All
      </ScopeButton>
    </div>
  )
}

function ScopeButton({
  selected,
  onClick,
  children,
}: {
  selected: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={cn(
        'min-h-11 min-w-11 rounded px-2.5 py-1 text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring lg:min-h-8',
        selected
          ? 'bg-secondary/10 text-secondary'
          : 'text-primary/70 hover:text-primary'
      )}
    >
      {children}
    </button>
  )
}

function CompanyDetail({
  company,
  role,
  appAccess,
  status,
  search,
}: {
  company: CompanyInfo
  role: string
  appAccess: string
  status: string
  search: string
}) {
  const [editingUserId, setEditingUserId] = useState<string | null>(null)
  const [inviteOpen, setInviteOpen] = useState(false)
  const [newDomain, setNewDomain] = useState('')
  const [showDomainInput, setShowDomainInput] = useState(false)
  const [deleteDomain, setDeleteDomain] = useState<{
    id: string
    domain: string
  } | null>(null)
  const [revokeInvitation, setRevokeInvitation] =
    useState<PortalInvitation | null>(null)
  const companyInviteButtonRef = useRef<HTMLButtonElement>(null)
  const domains = useAllowedDomains(company.id)
  const createDomain = useCreateAllowedDomain()
  const removeDomain = useDeleteAllowedDomain()
  const changeRole = useCompanyChangePortalUserRole(company.id)
  const changeAccess = useCompanyChangePortalUserAccess(company.id)
  const portalOgsm = isPortalOgsmEnabled()
  // The admin users list has no OGSM level; the company's portal users list does.
  const companyPortalUsers = useCompanyPortalUsers(company.id, portalOgsm)
  const changeOgsmAccess = useCompanyChangePortalUserOgsmAccess(company.id)
  const removeUser = useCompanyRemovePortalUser(company.id)
  const resend = useCompanyResendPortalInvitation(company.id)
  const revoke = useCompanyRevokePortalInvitation(company.id)

  const detailQuery = search.trim().toLowerCase()
  const personQuery = company.name.toLowerCase().includes(detailQuery)
    ? ''
    : detailQuery
  const filteredUsers = company.users.filter((user) =>
    userMatchesFilters(user, role, appAccess, status, personQuery)
  )
  const filteredInvitations = company.invitations.filter((invitation) =>
    invitationMatchesFilters(invitation, role, appAccess, status, personQuery)
  )

  const userRows: AdminDirectoryRow[] = filteredUsers.map((user) => {
    const displayName = user.name || nameFromEmail(user.email)
    return {
      id: user.id,
      name: displayName,
      email: user.email,
      initials: initialsFor(displayName),
      role: ROLE_LABELS.get(user.role) ?? user.role,
      appAccess: formatAllowedAppsSummary(user.allowedApps),
      status: statusForUser(user),
      activationLabel: `Edit ${displayName}`,
      onActivate: () => setEditingUserId(user.id),
    }
  })
  const invitationRows: AdminDirectoryRow[] = filteredInvitations.map(
    (invitation) => ({
      id: invitation.id,
      name: nameFromEmail(invitation.emailAddress),
      email: invitation.emailAddress,
      initials: initialsFor(invitation.emailAddress),
      role: invitation.invitedRole
        ? (ROLE_LABELS.get(invitation.invitedRole) ?? invitation.invitedRole)
        : 'Role not set',
      appAccess: formatAllowedAppsSummary(invitation.allowedApps),
      status: {
        kind: 'invited',
        label: 'Invited',
        detail: `Invited ${formatDate(invitation.createdAt)}`,
      },
      actions: (
        <>
          <Button
            variant="outline"
            size="sm"
            disabled={resend.isPending && resend.variables === invitation.id}
            onClick={() => void handleResend(invitation)}
          >
            {resend.isPending && resend.variables === invitation.id ? (
              <Loader2 className="size-3.5 animate-spin" aria-hidden="true" />
            ) : (
              <RefreshCw className="size-3.5" aria-hidden="true" />
            )}
            Resend
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="text-destructive hover:text-destructive"
            onClick={() => setRevokeInvitation(invitation)}
          >
            <Trash2 className="size-3.5" aria-hidden="true" />
            Revoke
          </Button>
        </>
      ),
    })
  )
  const rows = [...userRows, ...invitationRows]

  const editingUser = editingUserId
    ? (company.users.find((user) => user.id === editingUserId) ?? null)
    : null
  const editingOgsmAccess =
    portalOgsm && editingUser
      ? companyPortalUsers.data?.data.find((user) => user.id === editingUser.id)
          ?.ogsmAccess
      : undefined

  async function handleResend(invitation: PortalInvitation) {
    try {
      await resend.mutateAsync(invitation.id)
      showSuccess(`Invitation resent to ${invitation.emailAddress}`)
    } catch (error) {
      showError(error, 'Failed to resend invitation')
    }
  }

  async function handleRevoke() {
    if (!revokeInvitation) return
    try {
      await revoke.mutateAsync(revokeInvitation.id)
      showSuccess(`Invitation to ${revokeInvitation.emailAddress} revoked`)
      setRevokeInvitation(null)
    } catch (error) {
      showError(error, 'Failed to revoke invitation')
    }
  }

  async function handleCreateDomain() {
    const domain = newDomain.trim().toLowerCase()
    if (!DOMAIN_REGEX.test(domain)) return
    try {
      await createDomain.mutateAsync({ companyId: company.id, domain })
      showSuccess(`${domain} added`)
      setNewDomain('')
      setShowDomainInput(false)
    } catch (error) {
      showError(error, 'Failed to add domain')
    }
  }

  async function handleDeleteDomain() {
    if (!deleteDomain) return
    try {
      await removeDomain.mutateAsync({
        companyId: company.id,
        domainId: deleteDomain.id,
      })
      showSuccess(`${deleteDomain.domain} removed`)
      setDeleteDomain(null)
    } catch (error) {
      showError(error, 'Failed to remove domain')
    }
  }

  return (
    <section className="min-w-0 p-4 sm:p-6" aria-labelledby="company-heading">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div className="flex min-w-0 items-center gap-4">
          <Avatar className="size-14">
            <AvatarFallback className="bg-blue-100 text-primary">
              <Building2 className="size-6" aria-hidden="true" />
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <Typography id="company-heading" variant="h3" className="truncate">
              {company.name}
            </Typography>
            <Typography variant="body-sm" className="mt-1 text-primary/70">
              {company.users.length}{' '}
              {company.users.length === 1 ? 'user' : 'users'}
              {company.invitations.length > 0
                ? ` · ${company.invitations.length} pending`
                : ''}
            </Typography>
          </div>
        </div>
        <Button
          ref={companyInviteButtonRef}
          variant="outline"
          onClick={() => setInviteOpen(true)}
        >
          <UserRoundPlus className="size-4" aria-hidden="true" />
          Invite to company
        </Button>
      </div>

      <AdminUserTable
        rows={rows}
        ariaLabel={`${company.name} users and pending invitations`}
        emptyMessage="No users match these filters."
      />

      <div className="mt-7 border-t border-primary/10 pt-5">
        <Typography variant="body" className="mb-3 text-primary">
          Allowed domains
        </Typography>
        {domains.isError ? (
          <div className="flex items-center gap-3">
            <Typography variant="body-sm" className="text-destructive">
              Domains could not be loaded.
            </Typography>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void domains.refetch()}
            >
              Retry
            </Button>
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            {(domains.data ?? []).map((domain) => (
              <div
                key={domain.id}
                className="inline-flex min-h-9 items-center gap-2 rounded-lg bg-primary/5 px-3 py-1.5"
              >
                <Typography
                  as="span"
                  variant="body-sm"
                  className="text-primary"
                >
                  {domain.domain}
                </Typography>
                <button
                  type="button"
                  onClick={() =>
                    setDeleteDomain({ id: domain.id, domain: domain.domain })
                  }
                  aria-label={`Remove ${domain.domain}`}
                  className="rounded p-1 text-primary/55 hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <X className="size-3.5" aria-hidden="true" />
                </button>
              </div>
            ))}

            {showDomainInput ? (
              <div className="flex min-w-0 flex-1 items-center gap-2 sm:max-w-sm">
                <Input
                  value={newDomain}
                  onChange={(event) =>
                    setNewDomain(event.target.value.toLowerCase())
                  }
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') void handleCreateDomain()
                    if (event.key === 'Escape') {
                      setShowDomainInput(false)
                      setNewDomain('')
                    }
                  }}
                  aria-label="New allowed domain"
                  placeholder="company.com"
                  autoFocus
                />
                <Button
                  size="sm"
                  disabled={
                    !DOMAIN_REGEX.test(newDomain.trim()) ||
                    createDomain.isPending
                  }
                  onClick={() => void handleCreateDomain()}
                >
                  Add
                </Button>
              </div>
            ) : (
              <Button
                variant="outline"
                size="sm"
                className="border-dashed"
                onClick={() => setShowDomainInput(true)}
              >
                <Plus className="size-3.5" aria-hidden="true" />
                Add domain
              </Button>
            )}
          </div>
        )}
        <Typography
          variant="body-sm"
          className="mt-3 text-[11px] text-primary/70"
        >
          Users with email addresses from these domains can be invited to this
          company.
        </Typography>
      </div>

      <InviteUserSheet
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        mode="portco-picker"
        initialCompany={{ id: company.id, name: company.name }}
        returnFocusRef={companyInviteButtonRef}
        askOgsmAccess={portalOgsm}
      />

      {editingUser ? (
        <EditPortcoUserSheet
          open
          onOpenChange={(open) => {
            if (!open) setEditingUserId(null)
          }}
          user={editingUser}
          onChangeRole={(nextRole) =>
            changeRole.mutateAsync({ userId: editingUser.id, role: nextRole })
          }
          onChangeAccess={(allowedApps) =>
            changeAccess.mutateAsync({
              userId: editingUser.id,
              allowedApps,
            })
          }
          onRemove={() => removeUser.mutateAsync(editingUser.id)}
          ogsmAccess={editingOgsmAccess}
          onChangeOgsmAccess={
            portalOgsm
              ? (ogsmAccess) =>
                  changeOgsmAccess.mutateAsync({
                    userId: editingUser.id,
                    ogsmAccess,
                  })
              : undefined
          }
        />
      ) : null}

      <AlertDialog
        open={deleteDomain !== null}
        onOpenChange={(open) => {
          if (!open && !removeDomain.isPending) setDeleteDomain(null)
        }}
      >
        <AlertDialogContent className="max-w-sm">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove allowed domain?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteDomain
                ? `New invitations using @${deleteDomain.domain} will be blocked for ${company.name}. Existing users keep their access.`
                : ''}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={removeDomain.isPending}>
              Keep domain
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-white hover:bg-destructive/90"
              disabled={removeDomain.isPending}
              onClick={(event) => {
                event.preventDefault()
                void handleDeleteDomain()
              }}
            >
              Remove domain
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={revokeInvitation !== null}
        onOpenChange={(open) => {
          if (!open && !revoke.isPending) setRevokeInvitation(null)
        }}
      >
        <AlertDialogContent className="max-w-sm">
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke invitation?</AlertDialogTitle>
            <AlertDialogDescription>
              {revokeInvitation
                ? `The invitation to ${revokeInvitation.emailAddress} will stop working. You can send a new invitation later.`
                : ''}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={revoke.isPending}>
              Keep invitation
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-white hover:bg-destructive/90"
              disabled={revoke.isPending}
              onClick={(event) => {
                event.preventDefault()
                void handleRevoke()
              }}
            >
              Revoke invitation
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  )
}

function DirectorySkeleton() {
  return (
    <div className="grid min-h-[560px] grid-cols-[320px_minmax(0,1fr)] overflow-hidden rounded-xl border border-primary/15">
      <div className="border-r border-primary/10 p-4">
        <div className="h-10 animate-pulse rounded bg-primary/5" />
        {Array.from({ length: 5 }).map((_, index) => (
          <div
            key={index}
            className="mt-4 h-14 animate-pulse rounded bg-primary/5"
          />
        ))}
      </div>
      <div className="p-6">
        <div className="h-16 animate-pulse rounded bg-primary/5" />
        <div className="mt-6 h-64 animate-pulse rounded bg-primary/5" />
      </div>
    </div>
  )
}

function DirectoryError({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="flex min-h-72 flex-col items-center justify-center gap-3 rounded-xl border border-destructive/20 bg-destructive/5 px-6 text-center">
      <Typography variant="body" className="text-destructive">
        The PortCo directory could not be loaded.
      </Typography>
      <Button variant="outline" size="sm" onClick={onRetry}>
        Retry
      </Button>
    </div>
  )
}

function matchesAppAccess(allowedApps: AllowedApp[], filter: string) {
  if (filter === 'all') return true
  if (filter === 'none') return allowedApps.length === 0
  if (filter === 'both') {
    return allowedApps.includes('compass') && allowedApps.includes('ogsm')
  }
  return allowedApps.includes(filter === 'compass' ? 'compass' : 'ogsm')
}

function userMatchesFilters(
  user: AdminUser,
  role: string,
  appAccess: string,
  status: string,
  query: string
) {
  return (
    (!query ||
      user.name.toLowerCase().includes(query) ||
      user.email.toLowerCase().includes(query)) &&
    (role === 'all' || user.role === role) &&
    matchesAppAccess(user.allowedApps, appAccess) &&
    (status === 'all' ||
      (status === 'active' && user.isActive) ||
      (status === 'inactive' && !user.isActive))
  )
}

function invitationMatchesFilters(
  invitation: PortalInvitation,
  role: string,
  appAccess: string,
  status: string,
  query: string
) {
  return (
    (!query || invitation.emailAddress.toLowerCase().includes(query)) &&
    (role === 'all' || invitation.invitedRole === role) &&
    matchesAppAccess(invitation.allowedApps, appAccess) &&
    (status === 'all' || status === 'invited')
  )
}

function initialsFor(value: string) {
  return initialsOf(nameFromEmail(value))
}

function nameFromEmail(value: string) {
  const source = value.includes('@') ? value.split('@')[0] : value
  return source
    .replace(/[._+-]+/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => `${part[0]?.toUpperCase() ?? ''}${part.slice(1)}`)
    .join(' ')
}

function formatDate(timestamp: number) {
  return new Date(timestamp).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

function statusForUser(user: AdminUser): AdminDirectoryRow['status'] {
  return {
    kind: user.isActive ? 'active' : 'inactive',
    label: user.isActive ? 'Active' : 'Inactive',
    detail: `Member since ${formatDate(new Date(user.createdAt).getTime())}`,
  }
}
